/**
 * Contract Insights → Inspections: this round's status and the car's inspection history, read from
 * get_contract_inspections — the same payload the inspection page draws from, under the same cache
 * key. The full certificates, Inspect and Re-check live on that page; this tab links to it.
 *
 * Nothing here is guessed: the old "next due = last + one month" estimate is gone, because the
 * round and its window are the server's (PLAN §7).
 */

import { Link } from 'react-router-dom';
import { ArrowRight, Camera, ClipboardCheck, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/hooks/usePermissions';
import {
    KIND_LABEL,
    capturePath,
    contractInspectionPath,
    formatInspectionDay,
    formatKm,
    inProgressOnBoard,
    offersMonthly,
    roundName,
    windowLabel,
} from '@/lib/inspections';
import { useContractInspections, useInspectionBoard } from '@/components/inspections/inspectionApi';
import { AttendanceBadge, ConditionBadge, OpenCounts, Pill, ResultBadge } from '@/components/inspections/InspectionChip';

interface Props {
    contractId: number;
}

export default function ContractInsightInspections({ contractId }: Props) {
    const { data, isLoading, error } = useContractInspections(contractId);
    const { canAccessRoute } = usePermissions();
    // The inspection pages sit under the `inspections` key; an admin without it sees the summary only.
    const canOpen = canAccessRoute('/dashboard/inspections');
    // Inspect needs the board's in_progress for this contract (PLAN §5); only an admin who can open the pages is offered it.
    const board = useInspectionBoard(null, canOpen);

    if (isLoading || board.isLoading) return <Skeleton className="h-64 w-full" />;
    if (error || !data) {
        return <p className="py-8 text-center text-sm text-muted-foreground">Inspections could not be loaded{error ? `: ${(error as Error).message}` : '.'}</p>;
    }

    const { current, visits, header } = data;
    const vehicleId = header.vehicle_id;
    const monthly = offersMonthly(current.attendance, current.condition === 'held', inProgressOnBoard(board.data, contractId));

    return (
        <div className="space-y-6">
            <Card className="border border-border/40">
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0 pb-4">
                    <div>
                        <CardTitle>{current.period ? roundName(current.period) : 'No round yet'}</CardTitle>
                        <p className="mt-1 text-sm text-muted-foreground">
                            {current.period
                                ? `Window ${windowLabel(current.opens_on, current.closes_on)}`
                                : current.next_opens_on
                                  ? `The first round’s window is ${windowLabel(current.next_opens_on, current.next_closes_on)}.`
                                  : 'Monthly inspections start with the first round.'}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <AttendanceBadge attendance={current.attendance} late={current.late} />
                        <ConditionBadge condition={current.condition} />
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <dl className="grid gap-3 text-sm sm:grid-cols-3">
                        <div>
                            <dt className="text-muted-foreground">This round’s monthly</dt>
                            <dd className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                {current.inspection_id ? (
                                    <>
                                        {formatInspectionDay(current.inspected_on)}
                                        <ResultBadge result={current.result} />
                                    </>
                                ) : (
                                    '—'
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">Open on the car</dt>
                            <dd className="mt-0.5">
                                <OpenCounts fixNow={current.open_fix_now} fixSoon={current.open_fix_soon} />
                            </dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">Released</dt>
                            <dd className="mt-0.5">{current.released_on ? formatInspectionDay(current.released_on) : '—'}</dd>
                        </div>
                    </dl>
                    {canOpen && (
                        <div className="flex flex-wrap gap-2">
                            <Button asChild>
                                <Link to={contractInspectionPath(contractId)}>
                                    Open the inspection page
                                    <ArrowRight aria-hidden />
                                </Link>
                            </Button>
                            {vehicleId && monthly && (
                                <Button variant="outline" asChild>
                                    <Link to={capturePath(vehicleId, 'monthly', contractId)}>
                                        <Camera aria-hidden />
                                        Inspect
                                    </Link>
                                </Button>
                            )}
                            {vehicleId && (
                                <Button variant="outline" asChild>
                                    <Link to={capturePath(vehicleId, 'recheck', contractId)}>
                                        <RotateCcw aria-hidden />
                                        Re-check
                                    </Link>
                                </Button>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card className="border border-border/40">
                <CardHeader className="pb-4">
                    <CardTitle>Inspection history ({visits.length})</CardTitle>
                    <p className="text-sm text-muted-foreground">Every visit on this car, on this contract, others and between contracts.</p>
                </CardHeader>
                <CardContent>
                    {visits.length === 0 ? (
                        <div className="rounded-xl border-2 border-dashed bg-muted/20 py-12 text-center text-muted-foreground">
                            <ClipboardCheck className="mx-auto mb-3 h-8 w-8 opacity-30" aria-hidden />
                            <p className="text-sm font-medium">No inspection on this car yet</p>
                        </div>
                    ) : (
                        <ul className="divide-y">
                            {visits.map((v) => {
                                const body = (
                                    <div className="flex flex-wrap items-center justify-between gap-3 py-3">
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium">{formatInspectionDay(v.inspected_on)}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {v.kind ? KIND_LABEL[v.kind] : 'Notes only'}
                                                {v.between_contracts ? ' · between contracts' : v.contract_id !== contractId ? ` · contract #${v.contract_id}` : ''}
                                                {v.odometer_km !== null && ` · ${formatKm(v.odometer_km)}`}
                                                {v.photos.length > 0 && ` · ${v.photos.length} ${v.photos.length === 1 ? 'photo' : 'photos'}`}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {v.late && <Pill tone="orange">Late</Pill>}
                                            {v.released && <Pill tone="green">Released</Pill>}
                                            {v.kind && <ResultBadge result={v.result} />}
                                        </div>
                                    </div>
                                );
                                return (
                                    <li key={v.id}>
                                        {canOpen ? (
                                            <Link to={contractInspectionPath(contractId, v.id)} className="block hover:bg-accent/40">
                                                {body}
                                            </Link>
                                        ) : (
                                            body
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
